import { AppRoutes } from '../../common/constants';
import { Box, Card, CardContent, Skeleton, Tab, Tabs } from '@mui/material';
import { DEFAULT_ERROR_MESSAGE, DEFAULT_SAVED_MESSAGE } from '../../common/lib/fulcrumApi';
import { FNADetails, NeedCapacityType } from '../../common/types/analyses';
import { JSX, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { useIsFnaQuoted } from '../hooks/useIsFnaQuoted';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useUpdateFna } from '../../common/components/mutations';
import clsx from 'clsx';
import Content from '../../common/components/content';
import FnaPageHeaderSummary from '../components/fnaPageHeaderSummary';
import isNil from 'lodash/isNil';
import Layout from '../../common/components/layout';
import LayoutLoadingState from '../../common/components/layout/layoutLoadingState';
import NeedsStep from './steps/needsStep';
import omitBy from 'lodash/omitBy';
import StepFooter from '../../common/components/stepFooter';
import StructureStep from './steps/structureStep';
import ValuationStep from './steps/valuationStep';

const LAST_STEP_INDEX = 2;

/**
 * Buy and sell edit form for the FNA
 */
const BuyAndSellPage = (): JSX.Element => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { mutateAsync, isPending: mutationIsLoading } = useUpdateFna();
  const { data: fna } = useQuery<FNADetails>({
    queryKey: [`/FNADetail/${id}`],
    enabled: Boolean(id),
  });

  // Do not allow to edit needs if FNA is quoted (redirect to the FNA page)
  const { isBuyAndSellQuoted } = useIsFnaQuoted(Number(id));
  useEffect(() => {
    if (isBuyAndSellQuoted) {
      navigate(`${AppRoutes.FNAs}/${id}`);
    }
  }, [isBuyAndSellQuoted, navigate, id]);

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FNADetails>();

  const watchParties = watch('buyandsellParties');

  // Reset form when fna object changes
  useEffect(() => {
    const fnaRoleplayer = fna?.roleplayers[0];

    reset({
      ...fna,
      buyandsell: {
        ...fna?.buyandsell,
        // Apply default valuation values from FNA's roleplayer
        valuationValue: fna?.buyandsell?.valuationValue || fnaRoleplayer?.valuationValue,
        valuationMethodID: fna?.buyandsell?.valuationMethodID || fnaRoleplayer?.valuationMethodID,
        valuationBasisID: fna?.buyandsell?.valuationBasisID || fnaRoleplayer?.valuationBasisID,
      },
    });
  }, [fna, reset]);

  if (!fna) {
    return <LayoutLoadingState />;
  }

  const fnaRoleplayers =
    fna.roleplayers.map(({ roleplayerName }) => roleplayerName).join(', ') || '-';
  const breadcrumbs = [
    { label: 'Financial needs analyses', href: AppRoutes.FNAs },
    { label: fnaRoleplayers, href: `${AppRoutes.FNAs}/${fna.id}` },
  ];

  const commitFnaChanges = async (): Promise<void> => {
    await handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: FNADetails): Promise<void> => {
    try {
      await mutateAsync({
        ...data,
        buyandsell: {
          // Avoid null because API doesn't support it and return an error
          ...omitBy(data.buyandsell, isNil),
          fnaid: fna.id,
        },
        buyandsellParties: data.buyandsellParties?.map((party) => ({
          ...party,
          fnaBuyandsellID: fna.buyandsell?.id,
        })),
      });
      enqueueSnackbar(DEFAULT_SAVED_MESSAGE, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar((error as Error).message || DEFAULT_ERROR_MESSAGE, { variant: 'error' });
    }
  };

  // If there are more steps, go to the next one (otherwise save and go to calculations)
  const handleClickNext = async (): Promise<void> => {
    await commitFnaChanges();
    if (Object.values(errors).length > 0) return;
    if (activeTab < LAST_STEP_INDEX) {
      setActiveTab((value) => value + 1);
    } else {
      // Check if business interest is equal to 100% otherwise show error
      const businessInterestSum = watchParties?.reduce<number>(
        (accumulator, item) => Number((accumulator + item.interest).toFixed(2)),
        0,
      );
      if (businessInterestSum !== 100) {
        enqueueSnackbar('Business interest for all parties needs to add up to 100%', {
          variant: 'error',
        });
        return;
      }

      const hasSellingPartyWithoutInterest = watchParties?.find(
        (item) =>
          item.interest === 0 &&
          [NeedCapacityType.BuyingAndSelling, NeedCapacityType.Sell].includes(item.capacityID),
      );
      if (hasSellingPartyWithoutInterest) {
        enqueueSnackbar('Capacity cannot be "Selling" if business interest is 0%', {
          variant: 'error',
        });
        return;
      }

      await commitFnaChanges();
      navigate(`${AppRoutes.FNAs}/${fna.id}/buy-and-sell/needs`);
    }
  };

  const handleClickBack = (): void => {
    if (activeTab > 0) {
      setActiveTab((value) => value - 1);
    } else {
      navigate(`${AppRoutes.FNAs}/${fna.id}?tab=3`);
    }
  };

  const handleSaveAndExit = (): void => {
    commitFnaChanges().then(() => {
      navigate(AppRoutes.FNAs);
    });
  };

  const stepProps = {
    fna,
    control,
    setValue,
    commitFnaChanges,
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        <Box className="md:flex justify-between items-start md:space-x-4 space-y-8 md:space-y-0">
          <FnaPageHeaderSummary fna={fna} title="Buy-and-sell needs" />
          <LoadingButton
            loading={mutationIsLoading}
            className="flex-shrink-0"
            variant="contained"
            color="primary"
            onClick={handleSaveAndExit}
          >
            Save and exit
          </LoadingButton>
        </Box>
        <Tabs
          className="mt-12"
          variant="scrollable"
          scrollButtons="auto"
          value={activeTab}
          onChange={(_, newValue: number): void => setActiveTab(newValue)}
        >
          {/* Disable tabs to prevent jumping between them and allow navigation only by clicking Next button */}
          <Tab disabled label="Structure" />
          <Tab disabled label="Valuation" />
          <Tab disabled label="Party needs" />
        </Tabs>
        <Card>
          <CardContent className={clsx(mutationIsLoading && 'animate-pulse')}>
            {mutationIsLoading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : (
              <>
                {activeTab === 0 && <StructureStep {...stepProps} />}
                {activeTab === 1 && <ValuationStep {...stepProps} />}
                {activeTab === 2 && <NeedsStep {...stepProps} />}
              </>
            )}
          </CardContent>
        </Card>
        <StepFooter
          isLoading={mutationIsLoading}
          onClickNext={handleClickNext}
          onClickBack={handleClickBack}
          nextButtonTitle={activeTab === LAST_STEP_INDEX ? 'Calculate cover required' : 'Next'}
        />
      </Content>
    </Layout>
  );
};

export default BuyAndSellPage;
